import React, { useEffect, useState } from 'react';
import { BoxStyled } from './style';
import { NavigationButtons } from '../../../shared/components/navigationButtons';
import { Box, Divider, Modal } from '@mui/material';
import { TextWithInfoIcon } from '../../../shared/components/textWithInfoIcon';
import InfoIcon from '../../../assets/icons/infoIcon.svg';
import { ArcContainer } from '../../../shared/components/arcContainer';
import { DoubleTextBlueCardStyled } from '../../../shared/components/doubleTextBlueCardStyled';
import { SingleNumberCard } from '../../../shared/components/numberInput/numberCards/singleNumberCard';
import graphIcon from '../../../assets/icons/graphIcon.svg';
import dataMonitorIcon from '../../../assets/icons/dataMonitorIcon.svg';
import { AddOnInfoModal } from '../../../shared/components/modal/addOnInfoModal';

import { useDispatch } from 'react-redux';
import { useAppSelector } from '../../../store/hooks';
import { metaDataSelector } from '../../../store/globalSelectors';
import { setFaisySelection } from '../../../store/slices/metaDataSlice';
import { findRMR } from '../../../shared/utils/utilFunctions';

export const FaisyEquation = () => {
  const [open, setOpen] = useState(false);
  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);
  const { estimatingRequirements, patientInformation } =
    useAppSelector(metaDataSelector);
  const { faisyEquation } = estimatingRequirements;
  const { metric } = patientInformation;
  const { weight, estimatedDryWeight, height } = metric;
  const dispatch = useDispatch();
  const [rmr, setRMR] = useState(faisyEquation.rmr);

  useEffect(() => {
    const consideredWeight =
      estimatedDryWeight > 0 ? estimatedDryWeight : weight;
    let calculatedRMR = 0;
    // const consideredWeight = 10,height = 10 ;
    if (consideredWeight > 0 && height) {
      calculatedRMR = findRMR(
        faisyEquation.ve,
        faisyEquation.tMax,
        consideredWeight,
        height
      );
    }
    dispatch(
      setFaisySelection({
        ...faisyEquation,
        rmr: calculatedRMR
      })
    );
    setRMR(calculatedRMR);
  }, [faisyEquation.ve, faisyEquation.tMax]);

  return (
    <>
      <BoxStyled>
        {/* Top Bar (title + info icon) */}
        <TextWithInfoIcon
          data-testid={'faisyEqInfoPopupButton'}
          title={'Faisy'}
          icon={InfoIcon}
          fontSize={'1.5rem'}
          lineHeight={'1.375rem'}
          variant={'h1'}
          handleIconClick={() => handleOpen()}
        />
        <ArcContainer icon={dataMonitorIcon}>
          <Box sx={{ textAlign: 'center', mb: 2, mt: 5 }}>
            <Box
              sx={{
                border: '0.0625rem solid #D9D9D6',
                borderRadius: '0.9375rem',
                marginTop: '1.25rem'
              }}
              className="pt-16"
            >
              <SingleNumberCard
                data-testid={'test-VE'}
                width={'100%'}
                title={'VE'}
                marginVal={'0.625rem'}
                range={{ min: 0, max: 250 }}
                subHead={'(L/min)'}
                incrementDecrementVal={0.1}
                value={faisyEquation.ve}
                setValue={(val: number) =>
                  dispatch(
                    setFaisySelection({
                      ...faisyEquation,
                      ve: Number(val)
                    })
                  )
                }
              />
            </Box>
            <Divider
              sx={{
                backgroundColor: 'rgba(255,255,255,0.4)',
                my: 2,
                width: '100%',
                mx: 'auto'
              }}
            />
            <Box
              sx={{
                border: '0.0625rem solid #D9D9D6',
                borderRadius: '0.9375rem',
                marginTop: '1.25rem'
              }}
            >
              <SingleNumberCard
                width={'100%'}
                title={'TMAX'}
                marginVal={'0.625rem'}
                range={{ min: 0, max: 250 }}
                subHead={'(°C)'}
                incrementDecrementVal={0.1}
                value={faisyEquation.tMax}
                setValue={(val: number) =>
                  dispatch(
                    setFaisySelection({
                      ...faisyEquation,
                      tMax: Number(val)
                    })
                  )
                }
              />
            </Box>
          </Box>

          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2, mt: 5 }}>
            <DoubleTextBlueCardStyled
              heading1="RMR"
              label1={`${rmr} kcal`}
              icon={graphIcon}
            />
          </Box>
        </ArcContainer>
      </BoxStyled>

      <NavigationButtons
        nextDisabled={false}
        nextRoute={
          patientInformation.type === 'PAEDIATRIC'
            ? '/estimating-requirements/paediatric-energy'
            : '/estimating-requirements/protein'
        }
      />
      <Modal
        open={open}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <AddOnInfoModal
          heading1={`VE`}
          description1={` = Minute ventilation, litres per minute`}
          heading2={`TMAX`}
          description2={` = Maximum body temperature (in degrees Celcius) from previous 24 hours, or most recent body temperature`}
          heading3={`RMR`}
          description3={` = Resting Metabolic Rate`}
          title={`Reference:`}
          content={`Irish Nutrition and Dietetic Institute (2022) Nutrition Support
        Reference Guide.`}
          onClose={handleClose}
        />
      </Modal>
    </>
  );
};
